import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "../../../components/toast/useToast";
import PushService, {
    urlBase64ToUint8Array,
    arrayBufferToBase64,
    isPushSupported,
} from "./PushService";
import type { VapidKeyResponse } from "../generated/models/VapidKeyResponse";

// -----------------------------
// VAPID KEY QUERY
// -----------------------------

export const useVapidKeyQuery = () => {
    return useQuery<VapidKeyResponse, Error>({
        queryKey: ["vapidPublicKey"],
        queryFn: () => PushService.getVapidPublicKey(),
        enabled: isPushSupported(),
        staleTime: Infinity, // VAPID key doesn't change
    });
};

// -----------------------------
// SUBSCRIBE MUTATION
// -----------------------------

export const usePushSubscribeMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<void, Error, { vapidPublicKey: string }>({
        mutationFn: async ({ vapidPublicKey }) => {
            // Get service worker registration
            const registration = await navigator.serviceWorker.ready;

            // Subscribe to push
            const subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
            });

            // Extract keys
            const p256dh = arrayBufferToBase64(subscription.getKey('p256dh'));
            const auth = arrayBufferToBase64(subscription.getKey('auth'));

            // Send to backend
            await PushService.subscribe({
                endpoint: subscription.endpoint,
                p256dh,
                auth,
            });
        },

        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["pushSubscription"] });
            showToast("Push notifikace byly zapnuty.", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se zapnout push notifikace.", "error");
        },
    });
};

// -----------------------------
// UNSUBSCRIBE MUTATION
// -----------------------------

export const usePushUnsubscribeMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<void, Error, void>({
        mutationFn: async () => {
            const registration = await navigator.serviceWorker.ready;
            const subscription = await registration.pushManager.getSubscription();

            if (subscription) {
                // Unsubscribe from browser
                await subscription.unsubscribe();

                // Remove from backend
                await PushService.unsubscribe(subscription.endpoint);
            }
        },

        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["pushSubscription"] });
            showToast("Push notifikace byly vypnuty.", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se vypnout push notifikace.", "error");
        },
    });
};

// -----------------------------
// SUBSCRIPTION STATUS QUERY
// -----------------------------

export const usePushSubscriptionQuery = () => {
    return useQuery<PushSubscription | null, Error>({
        queryKey: ["pushSubscription"],
        queryFn: async () => {
            if (!isPushSupported()) return null;

            const registration = await navigator.serviceWorker.ready;
            return await registration.pushManager.getSubscription();
        },
        enabled: isPushSupported(),
    });
};
